import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { hideBackButton, isHosted, setBackButton, haptic, startParam } from "../platform/telegram";

/**
 * Navigation.
 *
 * Telegram has no bottom tab-bar convention, and the bottom of the viewport is
 * contested by MainButton and the keyboard — so the four top-level surfaces sit
 * in a rail directly under Telegram's header instead, per the design. Pushed
 * screens stack on top of whichever tab is active and are popped by Telegram's
 * own BackButton; no screen draws a back affordance of its own.
 *
 * A real router (history, URLs) buys nothing here: the app is a single webview
 * with no deep-link surface beyond `startapp`, which is handled once at launch.
 */

/** "inbox" exists only in the browser build's rail, mirroring the iOS tab set;
 * in the browser every tab except Home is the full-app gate anyway. */
export type Tab = "home" | "connect" | "study" | "inbox" | "profile";

/** Second-row section chips. State lives here so deep links and FroshBot's
 * navigate action can land on a section, not just a tab. "dash" exists only in
 * the browser build, mirroring the iOS Home sub-tab set. */
export type HomeSection = "dash" | "feed" | "explore" | "learn" | "earn" | "compete";
export type ConnectSection = "events" | "clubs" | "peers" | "matches" | "rivals";

export type Route =
  | { name: "courseDetail"; courseId: string }
  | { name: "professor"; professorId: string }
  | { name: "postDetail"; postId: string }
  | { name: "matchDetail"; userId: string }
  | { name: "publicProfile"; userId: string }
  | { name: "clubDetail"; clubId: string }
  | { name: "search" }
  | { name: "calendar" }
  | { name: "learn" }
  | { name: "earn" }
  | { name: "compete" }
  | { name: "explore" }
  | { name: "marketplace" }
  | { name: "rewards" }
  | { name: "settings" }
  | { name: "editProfile" }
  | { name: "horoscope" }
  | { name: "froshbot" };

interface NavState {
  tab: Tab;
  stack: Route[];
  homeSection: HomeSection;
  connectSection: ConnectSection;
  setTab: (tab: Tab) => void;
  setHomeSection: (section: HomeSection) => void;
  setConnectSection: (section: ConnectSection) => void;
  push: (route: Route) => void;
  pop: () => void;
  popToRoot: () => void;
}

const NavContext = createContext<NavState | null>(null);

const TABS: Tab[] = ["home", "connect", "study", "inbox", "profile"];
const HOME_SECTIONS: HomeSection[] = ["dash", "feed", "explore", "learn", "earn", "compete"];
const CONNECT_SECTIONS: ConnectSection[] = ["events", "clubs", "peers", "matches", "rivals"];

/**
 * Where to open.
 *
 * Telegram passes a `startapp` payload on deep links (`t.me/<bot>/<app>?startapp=study`),
 * which is how a bot message or a shared link lands someone on the right
 * surface instead of the feed. A section name ("earn", "matches") resolves to
 * its tab with that section selected; "froshbot" opens with the chat pushed.
 * Falls back to a `?tab=` query outside Telegram so the same paths are
 * reachable in a browser during development.
 */
function initialNav(): { tab: Tab; home: HomeSection; connect: ConnectSection; stack: Route[] } {
  const param =
    startParam() ??
    (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("tab") : null);

  const base = {
    tab: "home" as Tab,
    // Browser Home opens on Dash, mirroring iOS; Telegram keeps Feed first.
    home: (isHosted ? "feed" : "dash") as HomeSection,
    connect: "events" as ConnectSection,
    stack: [] as Route[],
  };
  // Browser deep link: /learn[/<course>[/<item>]] opens the Learn section
  // directly — the item URL is the atom of distribution and must land there.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/learn")) {
    return { ...base, home: "learn" };
  }
  if (!param) return base;
  if (TABS.includes(param as Tab)) return { ...base, tab: param as Tab };
  if (HOME_SECTIONS.includes(param as HomeSection)) {
    return { ...base, home: param as HomeSection };
  }
  if (CONNECT_SECTIONS.includes(param as ConnectSection)) {
    return { ...base, tab: "connect", connect: param as ConnectSection };
  }
  if (param === "froshbot") return { ...base, stack: [{ name: "froshbot" }] };
  if (
    param === "calendar" ||
    param === "search" ||
    param === "marketplace" ||
    param === "rewards" ||
    param === "settings" ||
    param === "editProfile" ||
    param === "horoscope"
  ) {
    return {
      ...base,
      tab: param === "calendar" || param === "search" ? "home" : "profile",
      stack: [{ name: param }],
    };
  }
  if (param.startsWith("user:")) {
    return { ...base, stack: [{ name: "publicProfile", userId: param.slice(5) }] };
  }
  if (param.startsWith("course:")) {
    return { ...base, tab: "study", stack: [{ name: "courseDetail", courseId: param.slice(7) }] };
  }
  if (param.startsWith("match:")) {
    return {
      ...base,
      tab: "connect",
      connect: "matches",
      stack: [{ name: "matchDetail", userId: param.slice(6) }],
    };
  }
  return base;
}

const INITIAL = initialNav();

export function NavProvider({ children }: { children: ReactNode }) {
  const [tab, setTabState] = useState<Tab>(INITIAL.tab);
  const [stack, setStack] = useState<Route[]>(INITIAL.stack);
  const [homeSection, setHomeSection] = useState<HomeSection>(INITIAL.home);
  const [connectSection, setConnectSection] = useState<ConnectSection>(INITIAL.connect);

  const pop = useCallback(() => {
    setStack((s) => s.slice(0, -1));
  }, []);

  const push = useCallback((route: Route) => {
    haptic.tap();
    setStack((s) => [...s, route]);
  }, []);

  const setTab = useCallback((next: Tab) => {
    haptic.select();
    setTabState(next);
    // Switching tabs discards the pushed stack. Preserving per-tab stacks is an
    // iOS convention that reads as "lost my place" in a webview with a single
    // system back button.
    setStack([]);
  }, []);

  const popToRoot = useCallback(() => setStack([]), []);

  // BackButton is Telegram's, lives in its header, and is the only back
  // affordance. It exists exactly when something is pushed.
  const depth = stack.length;
  useEffect(() => {
    if (depth === 0) {
      hideBackButton();
      return;
    }
    return setBackButton(() => setStack((s) => s.slice(0, -1)));
  }, [depth]);

  const value = useMemo(
    () => ({
      tab,
      stack,
      homeSection,
      connectSection,
      setTab,
      setHomeSection,
      setConnectSection,
      push,
      pop,
      popToRoot,
    }),
    [tab, stack, homeSection, connectSection, setTab, push, pop, popToRoot],
  );

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

export function useNav(): NavState {
  const nav = useContext(NavContext);
  if (!nav) throw new Error("useNav called outside NavProvider");
  return nav;
}
